import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { HttpErrorsService } from "@sharedV11/services/alerts/errors/http-errors.service";
import { SnackbarInfoService } from "@sharedV11/services/alerts/snackbar-info/snackbar-info.service";
import { ConectarApiService } from "@sharedV11/services/api/connection/conectar-api.service";
import { PanelService } from "@sharedV11/services/panel/panel.service";
import { Translator } from "@sharedV11/services/translator/translator.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-edit-tipo-precio-tx",
  templateUrl: "./edit-tipo-precio-tx.component.html",
  styleUrls: ["./edit-tipo-precio-tx.component.scss"],
})
export class EditTipoPrecioTxComponent implements OnInit, OnDestroy {
  @Input() pkId: string;
  @Input() mode: string;
  @Input() modificar: boolean;

  loading = true;
  translation: any;
  editPriceTypeForm: FormGroup;
  private destroy$ = new Subject<void>();
  private originalValues: any;

  constructor(
    private fb: FormBuilder,
    private translator: Translator,
    private httpErrorService: HttpErrorsService,
    private snackvar_service: SnackbarInfoService,
    private panelService: PanelService,
    private servicioAPI: ConectarApiService,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.translation = this.translator.GetTranslations();
    this.initializeForm();
    this.registerDirtyWatcher();
    this.manageForm(this.pkId, this.mode);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private static readonly FORBIDDEN_SEQUENCES = [
    ";",
    "/*",
    "*/",
    "@@",
    "!!",
    "--",
    "==",
    "//",
    "\\\\",
  ];

  private static forbiddenCharsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || typeof control.value !== "string") return null;
      const v = control.value;
      const found = EditTipoPrecioTxComponent.FORBIDDEN_SEQUENCES.find((seq) =>
        v.includes(seq)
      );
      return found ? { forbiddenChars: { value: found } } : null;
    };
  }

  static sanitizeForbiddenSequences(value: string): string {
    let s = (value ?? "").toString();
    EditTipoPrecioTxComponent.FORBIDDEN_SEQUENCES.forEach((seq) => {
      s = s.split(seq).join("");
    });
    return s;
  }

  onInputForbiddenSequences(event: Event, controlName: string): void {
    const input = event.target as HTMLInputElement | HTMLTextAreaElement;
    const value = input.value ?? "";
    const sanitized = EditTipoPrecioTxComponent.sanitizeForbiddenSequences(value);
    if (sanitized !== value) {
      const ctrl = this.editPriceTypeForm.get(controlName);
      if (ctrl) {
        ctrl.setValue(sanitized, { emitEvent: true });
        input.value = sanitized;
      }
    }
  }

  initializeForm() {
    const noPermitidos = EditTipoPrecioTxComponent.forbiddenCharsValidator();
    this.editPriceTypeForm = this.fb.group({
      pkId: [0],
      TipoPrecio: [
        "",
        [Validators.required, Validators.maxLength(50), noPermitidos],
      ],
      EtiquetaTrabajo: [
        "",
        [Validators.required, Validators.maxLength(50), noPermitidos],
      ],
    });
  }

  private registerDirtyWatcher() {
    if (!this.editPriceTypeForm) return;
    this.editPriceTypeForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.panelService.setDiscardChanges(false));
  }

  async manageForm(pkId: string, mode: string) {
    if (pkId && mode !== "I") {
      const paginator = {
        page: 1,
        pagelements: 1,
        orderby: "pkId",
        ordertype: "ASC",
      };
      const filters = [{ name: "pkId", value: pkId }];

      this.servicioAPI
        .getTaxonomyPriceTypesAsync(paginator, filters)
        .then((data) => {
          const lista =
            data?.DatosResult?.Lista ?? data?.DatosResult?.ListaTiposPrecio ?? [];
          if (lista.length) {
            const item = lista[0];
            const isDuplicate = mode === "D";
            const tipoPrecio = EditTipoPrecioTxComponent.sanitizeForbiddenSequences(
              (item.TipoPrecioTX ?? item.TipoPrecio ?? item.Nombre ?? "").toString()
            );
            const etiquetaTrabajo =
              EditTipoPrecioTxComponent.sanitizeForbiddenSequences(
                (
                  item.EtiquetaTrabajoTX ??
                  item.EtiquetaTrabajo ??
                  item.Descripcion ??
                  ""
                ).toString()
              );
            this.editPriceTypeForm.patchValue({
              pkId: isDuplicate ? 0 : +(item.pkId || 0),
              TipoPrecio: tipoPrecio,
              EtiquetaTrabajo: etiquetaTrabajo,
            });
            if (isDuplicate) this.mode = "I";
          }
          this.originalValues = { ...this.editPriceTypeForm.value };
          this.panelService.setDiscardChanges(true);
          this.loading = false;
        })
        .catch((error) => {
          this.httpErrorService.identificarErrores(error);
          this.loading = false;
        });
    } else {
      this.editPriceTypeForm.patchValue({
        pkId: 0,
        TipoPrecio: "",
        EtiquetaTrabajo: "",
      });
      this.originalValues = { ...this.editPriceTypeForm.value };
      this.loading = false;
      this.panelService.setDiscardChanges(true);
    }
  }

  discard() {
    if (this.editPriceTypeForm && this.originalValues) {
      this.editPriceTypeForm.reset(this.originalValues);
      this.panelService.setDiscardChanges(true);
    }
  }

  submit() {
    this.editPriceTypeForm.markAllAsTouched();
    if (this.editPriceTypeForm.invalid) return;

    const value = this.editPriceTypeForm.value;
    const body = {
      pkId: value.pkId || 0,
      TipoPrecio: EditTipoPrecioTxComponent.sanitizeForbiddenSequences(
        value.TipoPrecio ?? ""
      ),
      EtiquetaTrabajo: EditTipoPrecioTxComponent.sanitizeForbiddenSequences(
        value.EtiquetaTrabajo || ""
      ),
    };

    this.loading = true;
    this.servicioAPI
      .upsertTaxonomyPriceTypeAsync(body)
      .then((response) => {
        if (response?.Mensajes?.length) {
          response.Mensajes.forEach((m) => {
            const codeKey = `CodErr_TXTP_UPSERT_${m.CodigoMensaje}`;
            const fallbackCodeKey = `CodErr_${m.CodigoMensaje}`;
            const translated =
              this.translation?.[codeKey] ||
              this.translation?.[fallbackCodeKey] ||
              m.DescripcionMensaje;
            this.snackvar_service.openSnackBar(
              translated,
              "",
              "red-snackbar"
            );
          });
        } else {
          this.snackvar_service.openSnackBar(
            this.translation["MensajeSuccessSnackBar"] ||
              this.translation["GuardadoCorrectamente"] ||
              "Guardado correctamente",
            "",
            "green-snackbar"
          );
        }
        this.closePanel();
      })
      .catch((error) => {
        this.httpErrorService.identificarErrores(error);
        this.loading = false;
      });
  }

  getErrorMessage(field: string) {
    const control = this.editPriceTypeForm.get(field);
    if (!control?.errors) return "";
    if (control.hasError("required")) {
      return this.translation?.CampoRequerido || "Campo requerido";
    }
    if (control.hasError("maxlength")) {
      const m = control.getError("maxlength");
      return (
        (this.translation?.LongitudMaxima || "Longitud máxima") +
        ": " +
        (m?.requiredLength || "")
      );
    }
    if (control.hasError("forbiddenChars")) {
      const err = control.getError("forbiddenChars");
      const char = err?.value ?? "";
      const msg =
        this.translation?.CaracteresNoPermitidos ||
        "Caracteres o combinación no permitida por el backend";
      return char ? `${msg}: «${char}»` : msg;
    }
    return "Error";
  }

  validateFields(field: string) {
    const control = this.editPriceTypeForm.get(field);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  closePanel() {
    this.loading = false;
    this.panelService.setSavePanel(null);
    this.panelService.setDiscardChanges(true);
    this.panelService.setClosePanel(null);
  }
}
